/* eslint-disable unicorn/consistent-function-scoping */
import { useState, useCallback, useRef, useEffect } from 'react';
import {
  AddressElement,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { Address, StripeAddressElementChangeEvent } from '@stripe/stripe-js';
import * as Sentry from '@sentry/react';

import {
  selectAmounts,
  selectMentor,
  selectNumberOfLicenses,
  selectTrackGroup,
  selectSessionId,
  setEmail,
  setPaymentIntentId,
  setSessionId,
} from '../../../features/signUp/signUpSlice';
import { handleError } from '../../../services/ErrorService';
import { store, useAppSelector } from '../../../stores/AppStore';
import {
  useCreatePaymentIntentMutation,
  useConfirmTrialPaymentMethodMutation,
  useGetPriceConfigQuery,
  useCreateSignUpSessionMutation,
  useUpdateSignUpSessionMutation,
} from '../../../services/apiSlice';
import {
  emailPattern,
  getStripeAddressElementOptions,
  getStripePaymentElementOptions,
  TRIAL_PERIOD_DAYS,
} from '../../../constants';
import {
  logPaymentDebugInfo,
  validatePaymentRequirements,
} from '../../../utils/payment-debug';

import OrderDetails from './OrderDetails';
import { Link } from 'react-router-dom';
import { BillingPeriod } from '../../../types';

interface EmbeddedCardFormProperties {
  onNextStep: (paymentType: 'trial' | 'payment' | 'discounted') => void;
  onPreviousStep: () => void;
}

const EmbeddedCardForm = ({
  onNextStep,
  onPreviousStep,
}: EmbeddedCardFormProperties) => {
  const stripe = useStripe();
  const elements = useElements();
  const [{ addressBlocked, cardBlocked }, setBlocked] = useState({
    addressBlocked: true,
    cardBlocked: true,
  });
  const mentor = useAppSelector(selectMentor);
  const sessionId = useAppSelector(selectSessionId);
  const [address, setAddress] = useState<Address>();
  const [name, setName] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);
  const amounts = useAppSelector(selectAmounts);
  const numberOfLicenses = useAppSelector(selectNumberOfLicenses);
  const trackGroup = useAppSelector(selectTrackGroup);
  const [createPaymentIntentMutation, { error: paymentIntentError }] =
    useCreatePaymentIntentMutation();
  const [confirmTrialPaymentMethodMutation] =
    useConfirmTrialPaymentMethodMutation();
  const [createSignUpSession] = useCreateSignUpSessionMutation();
  const [updateSignUpSession] = useUpdateSignUpSessionMutation();
  const {
    data: priceConfig,
    isLoading: isPriceConfigLoading,
    error: priceConfigError,
    refetch: refetchPriceConfig,
  } = useGetPriceConfigQuery();

  const isEmailValid = emailPattern.test(mentor.email);

  // Keep the sessionId ref in sync with Redux state
  useEffect(() => {
    currentSessionIdReference.current = sessionId;
  }, [sessionId]);

  const getPriceId = (
    billingPeriod: BillingPeriod,
    licenseCount: number,
  ): string => {
    if (!priceConfig) {
      console.error('Price configuration not loaded', {
        isLoading: isPriceConfigLoading,
        hasError: !!priceConfigError,
      });
      return '';
    }

    const priceMap = {
      month: priceConfig.monthly,
      '3month': priceConfig['3monthly'],
      year: priceConfig.yearly,
    };

    const priceIds = priceMap[billingPeriod];
    const priceId = priceIds?.[licenseCount];

    if (!priceId) {
      console.error(
        `No price ID found for ${billingPeriod} billing with ${licenseCount} licenses`,
        {
          billingPeriod,
          licenseCount,
          availablePrices: priceMap[billingPeriod],
          fullPriceConfig: priceConfig,
        },
      );

      // Show user-friendly error
      handleError(
        new Error(
          `Pricing unavailable for ${licenseCount} license${
            licenseCount > 1 ? 's' : ''
          } with ${billingPeriod} billing. Please try refreshing the page or contact support.`,
        ),
        {
          displayedError:
            'Pricing information temporarily unavailable. Please refresh the page.',
        },
      );
      return '';
    }

    return priceId;
  };

  const handleAddressChange = async (
    event: StripeAddressElementChangeEvent,
  ) => {
    // Check if we have enough address information for tax calculation
    // We need city, state, and postal_code - don't wait for Stripe's "complete" flag
    const hasRequiredFields =
      event.value.address &&
      event.value.address.city &&
      event.value.address.state &&
      event.value.address.postal_code;

    if (!hasRequiredFields) {
      setBlocked((previous) => ({ ...previous, addressBlocked: true }));
      return;
    }

    // Check if address has changed (for tax calculation purposes)
    const addressChanged = !(
      address &&
      address.city === event.value.address.city &&
      address.postal_code === event.value.address.postal_code &&
      address.state === event.value.address.state
    );

    // Only save address if it has actually changed
    if (addressChanged) {
      setName(event.value.name);
      setAddress(event.value.address);
    }

    // Always update the blocked state based on Stripe's complete status
    // This ensures the payment button gets unblocked when address becomes complete
    setBlocked((previous) => ({
      ...previous,
      addressBlocked: !event.complete,
    }));
  };

  // Create or update sign-up session when email changes
  const emailTimeoutReference = useRef<NodeJS.Timeout>();
  const isCreatingSessionReference = useRef(false);
  const currentSessionIdReference = useRef<string | null>(null);

  const handleEmailChange = useCallback(
    async (email: string) => {
      // Clear any pending timeout
      if (emailTimeoutReference.current) {
        clearTimeout(emailTimeoutReference.current);
      }

      // Update email in store immediately
      store.dispatch(setEmail(email));

      // Only create/update session if email is valid
      if (!emailPattern.test(email)) {
        return;
      }

      // Debounce session creation/update by 1 second
      emailTimeoutReference.current = setTimeout(async () => {
        // Check if a session creation is already in progress
        if (isCreatingSessionReference.current) {
          console.log('Session creation already in progress, skipping...');
          return;
        }

        try {
          // Collect current form data
          const formData = {
            mentor: {
              email,
              firstName: mentor.firstName,
              lastName: mentor.lastName,
              phone: mentor.phone,
            },
            numberOfLicenses,
            trackGroup,
            amounts,
          };

          // Use refs to get the most current sessionId
          const currentSessionId =
            sessionId || currentSessionIdReference.current;

          if (currentSessionId) {
            // Update existing session
            await updateSignUpSession({
              sessionId: currentSessionId,
              email, // Include email to handle incremental typing
              currentStep: 1,
              formData,
            }).unwrap();

            console.log('Updated sign-up session:', currentSessionId);
          } else {
            // Mark that we're creating a session
            isCreatingSessionReference.current = true;

            // Create new session
            const result = await createSignUpSession({
              email,
              currentStep: 1, // Payment step
              formData,
            }).unwrap();

            // Store the sessionId in both Redux and ref
            store.dispatch(setSessionId(result.sessionId));
            currentSessionIdReference.current = result.sessionId;
            console.log('Created sign-up session:', result.sessionId);
          }
        } catch (error) {
          console.error('Failed to create/update sign-up session:', error);
          // Don't show error to user - session creation is for recovery purposes
        } finally {
          // Always clear the flag when done
          isCreatingSessionReference.current = false;
        }
      }, 1000);
    },
    [
      sessionId,
      mentor,
      numberOfLicenses,
      trackGroup,
      amounts,
      createSignUpSession,
      updateSignUpSession,
    ],
  );

  type PaymentSetupResult =
    | { type: 'payment_required'; clientSecret: string }
    | {
        type: 'trial_setup_required';
        clientSecret: string;
        setupIntentId: string;
        subscriptionId: string;
      }
    | { type: 'already_paid' };

  const getClientSecret = async (): Promise<PaymentSetupResult | undefined> => {
    setIsLoading(true);
    try {
      if (!amounts?.total && amounts?.total !== 0) {
        const errorMessage =
          'Could not process payment as total amount was not calculated.';
        handleError(new Error(errorMessage), { displayedError: errorMessage });
        return undefined;
      }
      if (!address || !name) {
        const errorMessage =
          'Could not process payment as name or address is missing.';
        handleError(new Error(errorMessage), { displayedError: errorMessage });
        return undefined;
      }
      const billingPeriod = (amounts.billingPeriod as BillingPeriod) ?? 'month';
      const result = await createPaymentIntentMutation({
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        amount: amounts.total,
        email: mentor.email,
        address,
        name,
        billingPeriod,
        priceId: getPriceId(billingPeriod, numberOfLicenses),
        licenseCount: numberOfLicenses,
        discountCode: amounts.discountCode,
        trialPeriodDays: TRIAL_PERIOD_DAYS,
      }).unwrap();

      if (paymentIntentError) {
        handleError(paymentIntentError);
        setIsLoading(false);
        return undefined;
      }
      if (!result) {
        setIsLoading(false);
        return undefined;
      }

      // Handle trial subscription with SetupIntent
      if (
        'requiresSetupConfirmation' in result &&
        result.requiresSetupConfirmation
      ) {
        console.log(
          'Trial subscription created, requires payment method setup',
        );
        const trialResult = result as {
          requiresPayment: true;
          requiresSetupConfirmation: true;
          client_secret: string;
          setupIntentId: string;
          subscriptionId: string;
          isTrial: true;
        };
        store.dispatch(setPaymentIntentId(trialResult.subscriptionId));
        setIsLoading(false);

        return {
          type: 'trial_setup_required',
          clientSecret: trialResult.client_secret,
          setupIntentId: trialResult.setupIntentId,
          subscriptionId: trialResult.subscriptionId,
        };
      }

      // Handle regular payment (immediate charging)
      if (result.requiresPayment && 'paymentIntentId' in result) {
        const paymentResult = result as {
          requiresPayment: true;
          client_secret: string;
          paymentIntentId: string;
        };
        store.dispatch(setPaymentIntentId(paymentResult.paymentIntentId));
        return {
          type: 'payment_required',
          clientSecret: paymentResult.client_secret,
        };
      }

      // This should not happen with the cleaned up flows
      throw new Error(
        'Unexpected response format from payment intent creation',
      );
    } catch (error) {
      handleError(error);
      setIsLoading(false);
      return undefined;
    }
  };

  const handlePayment = async () => {
    // Debug logging for mobile issues
    const debugInfo = logPaymentDebugInfo();
    const validation = validatePaymentRequirements();

    if (!validation.isValid) {
      console.error('Payment validation failed:', validation.issues);
      Sentry.captureMessage('Payment Validation Failed', {
        level: 'error',
        tags: { payment_error: true, validation_failed: true },
        extra: {
          issues: validation.issues,
          debugInfo,
          userAgent: navigator.userAgent,
        },
      });
      handleError(new Error('Payment validation failed'), {
        displayedError: `Payment setup issue: ${validation.issues.join(
          ', ',
        )}. Please try refreshing the page.`,
      });
      return;
    }

    if (!stripe || !elements) {
      const errorMessage =
        'Could not process payment as a critical detail is missing.';
      console.error('Payment initialization failed:', {
        stripe: !!stripe,
        elements: !!elements,
      });
      Sentry.captureMessage('Payment Initialization Failed', {
        level: 'error',
        tags: { payment_error: true, initialization_failed: true },
        extra: {
          stripe: !!stripe,
          elements: !!elements,
          debugInfo,
          userAgent: navigator.userAgent,
        },
      });
      handleError(new Error(errorMessage), {
        displayedError:
          errorMessage +
          ' Please try again or contact us at help@thinktankprep.com',
      });
      return;
    }
    setIsLoading(true);

    try {
      // For Apple Pay and other wallet payments, we need to minimize async operations
      // before the payment confirmation to maintain user activation context

      // First, submit the form to validate it synchronously
      const submitResult = await elements.submit();
      if (submitResult?.error) {
        console.error('Elements submit failed:', submitResult.error);
        Sentry.captureMessage('Elements Submit Failed', {
          level: 'error',
          tags: {
            payment_error: true,
            elements_submit_failed: true,
            stripe_error_type: submitResult.error.type,
          },
          extra: {
            errorMessage: submitResult.error.message,
            debugInfo,
            userAgent: navigator.userAgent,
          },
        });
        handleError(submitResult.error, {
          displayedError:
            submitResult.error.message || 'Payment form validation failed',
        });
        setIsLoading(false);
        return;
      }

      // Now get the client secret and handle the payment/setup
      const setupResult = await getClientSecret();
      if (!setupResult) {
        handleError(new Error('Payment setup could not be completed'), {
          displayedError:
            'Failed to process payment. Please try again or contact us at help@thinktankprep.com',
        });
        setIsLoading(false);
        return;
      }

      // Handle trial setup required
      if (setupResult.type === 'trial_setup_required') {
        console.log('Confirming setup intent for trial subscription');
        const confirmSetupResponse = await stripe.confirmSetup({
          elements,
          clientSecret: setupResult.clientSecret,
          confirmParams: {
            return_url: `${process.env.REACT_APP_BASE_URL}/sign-up`,
          },
          redirect: 'if_required',
        });

        if (confirmSetupResponse.error) {
          console.error(
            'Setup confirmation failed:',
            confirmSetupResponse.error,
          );
          Sentry.captureMessage('Trial Setup Confirmation Failed', {
            level: 'error',
            tags: {
              payment_error: true,
              setup_confirmation_failed: true,
              trial_setup: true,
              stripe_error_type: confirmSetupResponse.error.type,
            },
            extra: {
              errorMessage: confirmSetupResponse.error.message,
              errorCode: confirmSetupResponse.error.code,
              debugInfo,
              userAgent: navigator.userAgent,
            },
          });

          let displayedError =
            'Payment method validation failed. Please try again.';
          switch (confirmSetupResponse.error.type) {
            case 'validation_error':
            case 'card_error': {
              displayedError =
                confirmSetupResponse.error.message || displayedError;
              break;
            }
            case 'api_error':
            case 'api_connection_error':
            case 'idempotency_error':
            case 'authentication_error': {
              displayedError =
                'An error occurred while validating your payment method. Please try again.';
              break;
            }
            case 'rate_limit_error': {
              displayedError =
                'Too many requests. Please try again in a moment.';
              break;
            }
            case 'invalid_request_error': {
              displayedError =
                'An error occurred on our end. Please contact support.';
              break;
            }
          }

          handleError(confirmSetupResponse.error, { displayedError });
          setIsLoading(false);
          return;
        }

        // Setup confirmed successfully, now attach payment method to subscription
        const setupIntent = confirmSetupResponse.setupIntent;
        if (setupIntent.status === 'succeeded' && setupIntent.payment_method) {
          try {
            await confirmTrialPaymentMethodMutation({
              setupIntentId: setupResult.setupIntentId,
              subscriptionId: setupResult.subscriptionId,
              paymentMethodId:
                typeof setupIntent.payment_method === 'string'
                  ? setupIntent.payment_method
                  : setupIntent.payment_method.id,
            }).unwrap();

            console.log(
              'Trial subscription payment method confirmed successfully',
            );

            // Update session with payment status
            if (sessionId) {
              try {
                await updateSignUpSession({
                  sessionId,
                  currentStep: 1,
                  status: 'payment_completed',
                  paymentIntentId: setupResult.subscriptionId,
                }).unwrap();
              } catch (error) {
                console.error(
                  'Failed to update session after trial setup:',
                  error,
                );
              }
            }

            setIsLoading(false);
            onNextStep('trial');
            return;
          } catch (confirmError) {
            console.error(
              'Failed to confirm trial payment method:',
              confirmError,
            );
            Sentry.captureMessage('Trial Payment Method Confirmation Failed', {
              level: 'error',
              tags: {
                payment_error: true,
                trial_confirmation_failed: true,
              },
              extra: {
                setupIntentId: setupResult.setupIntentId,
                subscriptionId: setupResult.subscriptionId,
                confirmError,
                debugInfo,
                userAgent: navigator.userAgent,
              },
            });
            handleError(
              new Error('Failed to confirm payment method for trial'),
              {
                displayedError:
                  'Payment method validation succeeded, but trial activation failed. Please contact support.',
              },
            );
            setIsLoading(false);
            return;
          }
        } else {
          console.error(
            'Setup intent not in expected state:',
            setupIntent.status,
          );
          handleError(new Error('Setup intent not succeeded'), {
            displayedError: 'Payment method setup failed. Please try again.',
          });
          setIsLoading(false);
          return;
        }
      }

      // Handle regular payment (immediate charging)
      if (setupResult.type === 'payment_required') {
        console.log('Confirming payment with secret');
        const confirmPaymentResponse = await stripe.confirmPayment({
          elements,
          clientSecret: setupResult.clientSecret,
          confirmParams: {
            return_url: `${process.env.REACT_APP_BASE_URL}/sign-up`,
          },
          redirect: 'if_required',
        });

        if (confirmPaymentResponse.error) {
          console.error(
            'Payment confirmation failed:',
            confirmPaymentResponse.error,
          );
          Sentry.captureMessage('Payment Confirmation Failed', {
            level: 'error',
            tags: {
              payment_error: true,
              confirmation_failed: true,
              stripe_error_type: confirmPaymentResponse.error.type,
            },
            extra: {
              errorMessage: confirmPaymentResponse.error.message,
              errorCode: confirmPaymentResponse.error.code,
              debugInfo,
              userAgent: navigator.userAgent,
            },
          });
          let displayedError: string | undefined = '';
          switch (confirmPaymentResponse.error.type) {
            case 'validation_error':
            case 'card_error': {
              displayedError = confirmPaymentResponse.error.message;
              break;
            }
            case 'api_error':
            case 'api_connection_error':
            case 'idempotency_error':
            case 'authentication_error': {
              displayedError =
                'An error occurred while processing your payment. If this issue persists, please contact the support.';
              break;
            }
            case 'rate_limit_error': {
              displayedError =
                'Too many request while trying to process your payment. Please try again later.';
              break;
            }
            case 'invalid_request_error': {
              displayedError =
                'An error occured on our end while processing your payment. Please contact the support.';
              break;
            }
            default: {
              displayedError =
                'An unexpected error occurred. Please try again.';
              break;
            }
          }
          handleError(confirmPaymentResponse.error, {
            displayedError,
          });
          setIsLoading(false);
          return;
        }

        console.log('Payment confirmed successfully');

        // Update session with payment status
        if (sessionId) {
          try {
            await updateSignUpSession({
              sessionId,
              currentStep: 1,
              status: 'payment_completed',
              paymentIntentId: confirmPaymentResponse.paymentIntent?.id,
            }).unwrap();
          } catch (error) {
            console.error('Failed to update session after payment:', error);
          }
        }

        setIsLoading(false);
        onNextStep('payment');
        return;
      }

      // Handle already paid invoices
      if (setupResult.type === 'already_paid') {
        console.log('Payment already completed, proceeding to next step');
        setIsLoading(false);
        onNextStep('payment');
        return;
      }
    } catch (error) {
      console.error('Payment process failed with exception:', error);
      Sentry.captureException(
        error instanceof Error ? error : new Error('Unknown payment error'),
        {
          tags: {
            payment_error: true,
            payment_exception: true,
          },
          extra: {
            debugInfo,
            userAgent: navigator.userAgent,
            errorType: typeof error,
          },
        },
      );
      handleError(
        error instanceof Error ? error : new Error('Unknown payment error'),
        {
          displayedError:
            'Payment processing failed. Please try again or contact support.',
        },
      );
      setIsLoading(false);
    }
  };

  return (
    <div className="mt-[-8px] md:mt-0 flex flex-col gap-2 md:gap-4 w-full h-full">
      {/* Pricing Error Message */}
      {priceConfigError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-red-600 font-semibold">
              ⚠️ Pricing Unavailable
            </span>
          </div>
          <p className="text-red-700 text-sm">
            Unable to load current pricing information. Please refresh the page
            or try again later.
          </p>
          <button
            onClick={() => refetchPriceConfig()}
            className="mt-2 text-red-600 text-sm underline hover:no-underline"
          >
            Try Again
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Order Details */}
        <OrderDetails
          address={address}
          isApplyCouponBlocked={isLoading}
          setIsLoading={setIsLoading}
        />

        {/* Payment Elements */}
        {amounts?.amount && amounts?.amount > 0 ? (
          <div className={`flex flex-col gap-2 w-full`}>
            <h1 className="text-2xl md:text-3xl font-medium w-full pb-2 md:pb-4">
              Step 2: Payment Details
            </h1>
            <div data-field="name" className="p-Field">
              <label
                className="p-FieldLabel Label Label--empty font-"
                htmlFor="Field-emailInput"
              >
                Email
              </label>
              <div>
                <input
                  type="email"
                  id="Field-emailInput"
                  name="email"
                  autoComplete="email"
                  placeholder="Email address"
                  aria-invalid="false"
                  aria-required="true"
                  className="p-Input-input Input Input--empty border border-slate-200 w-full p-3 rounded-l"
                  value={mentor.email}
                  onChange={(event) => {
                    handleEmailChange(event.target.value.trim().toLowerCase());
                  }}
                />
              </div>
              <div className="AnimateSinglePresence"></div>
            </div>
            <AddressElement
              id="address-element"
              options={getStripeAddressElementOptions({
                // name: mentor.firstName + ' ' + mentor.lastName,
              })}
              onChange={handleAddressChange}
            />
            <PaymentElement
              id="payment-element"
              options={getStripePaymentElementOptions()}
              onChange={(event) =>
                setBlocked((previous) => ({
                  ...previous,
                  cardBlocked: !event.complete,
                }))
              }
            />
          </div>
        ) : (
          <></>
        )}
      </div>

      {/* Action Buttons */}
      <div
        className={`flex gap-2 pt-4 md:pt-6 w-full justify-center md:justify-between items-center`}
      >
        <button
          data-testid={`back-button`}
          type="button"
          className="
              px-10 py-2 w-max rounded-full border font-medium
              transition-all duration-300 ease-in-out
              hover:shadow hover:outline hover:outline-tttDefault hover:outline-[1px]
              disabled:opacity-50 disabled:cursor-not-allowed
            "
          onClick={onPreviousStep}
        >
          Back
        </button>
        <button
          data-testid={`continue-button`}
          onClick={handlePayment}
          disabled={
            isLoading ||
            isPriceConfigLoading ||
            !priceConfig ||
            numberOfLicenses > 3 ||
            addressBlocked ||
            cardBlocked ||
            !isEmailValid ||
            !trackGroup
          }
          title={
            isPriceConfigLoading
              ? 'Loading pricing information...'
              : priceConfig
              ? numberOfLicenses > 3
                ? 'For 4+ licenses, please contact us to set up your subscriptions'
                : addressBlocked
                ? 'Please enter your billing address first'
                : cardBlocked
                ? 'Please enter your card details'
                : trackGroup
                ? isEmailValid
                  ? ''
                  : 'Please enter a valid email address'
                : 'Please choose a learning path'
              : 'Pricing information unavailable. Please refresh the page.'
          }
          className="
            px-10 py-2 w-max rounded-full
            font-medium text-white bg-tttDefault
            transition-all duration-300 ease-in-out
            hover:shadow hover:bg-opacity-90
            disabled:opacity-50 disabled:cursor-not-allowed
          "
        >
          {isPriceConfigLoading
            ? 'Loading Pricing...'
            : numberOfLicenses > 3
            ? 'Contact Us'
            : 'Start Free Trial'}
        </button>
      </div>

      <p className="text-xs text-gray-600">
        By clicking "Start Free Trial" you acknowledge that you have read and
        agree to the Think Tank's{' '}
        <Link to="/privacy-policy" target="_blank" className="text-tttDefault">
          Privacy Policy
        </Link>{' '}
        and{' '}
        <Link
          to="/terms-and-conditions"
          target="_blank"
          className="text-tttDefault"
        >
          Terms of Service
        </Link>
        . Your {TRIAL_PERIOD_DAYS}-day free trial will begin immediately. You
        can cancel anytime during the trial at no cost. After the trial period,
        you will be automatically charged at the selected billing frequency
        unless cancelled.
      </p>
    </div>
  );
};

export default EmbeddedCardForm;
